/**
 * Server-based License Service
 * Handles all license operations through the server proxy for security
 */

import type { 
  LicenseInfo, 
  DeviceInfo,
  LicenseValidationResult,
  LicenseActivationResult 
} from './interfaces';

export class ServerLicenseService {
  private readonly proxyUrl: string;
  
  constructor(proxyUrl?: string) {
    this.proxyUrl = proxyUrl || import.meta.env.VITE_PROXY_URL || 'https://appstore.junlim.org';
  }

  /**
   * Verify a license key through the server
   */
  async verifyLicense(licenseKey: string): Promise<LicenseValidationResult> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/license/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ licenseKey })
      });

      const data = await response.json();

      if (data.valid) {
        const license: LicenseInfo = {
          licenseKey,
          provider: 'gumroad',
          productId: data.productId,
          productName: data.productName,
          email: data.email,
          purchaseDate: new Date(data.purchaseDate),
          expiresAt: data.subscriptionEndedAt ? new Date(data.subscriptionEndedAt) : undefined,
          uses: data.uses || 0,
          maxDevices: 5,  // Same limit for both monthly and yearly subscriptions
          isActive: true,
          lastValidatedAt: new Date(),
          devices: data.devices || [],
          metadata: {
            isSubscription: data.isSubscription,
            subscriptionId: data.subscriptionId,
            price: data.price,
            currency: data.currency,
            saleId: data.saleId,
            orderId: data.orderId,
            currentDevices: data.currentDevices || [],
            activeDeviceCount: data.activeDeviceCount  // Add active device count from server
          }
        };

        return {
          isValid: true,
          license,
          fromCache: data.fromCache || false,
          validatedAt: new Date()
        };
      } else {
        return {
          isValid: false,
          error: {
            type: data.error === 'invalid_license' ? 'invalid_license' : 'validation_failed',
            code: data.error || 'invalid_license',
            message: data.message || '유효하지 않은 라이선스입니다.',
            retryable: false,
            details: data.details
          },
          validatedAt: new Date()
        };
      }
    } catch (error) {
      return {
        isValid: false,
        error: {
          type: 'network_error',
          code: 'network_error',
          message: '라이선스 검증 중 네트워크 오류가 발생했습니다.',
          retryable: true,
          details: error instanceof Error ? error.message : 'Unknown error'
        },
        validatedAt: new Date()
      };
    }
  }

  /**
   * Check if a license can be activated on a device
   */
  async checkActivation(licenseKey: string, deviceId: string): Promise<{
    canActivate: boolean;
    reason?: string;
    message: string;
    currentDevices?: number;
    maxDevices?: number;
    alreadyActivated?: boolean;
  }> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/license/check-activation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ licenseKey, deviceId })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        canActivate: false,
        reason: 'network_error',
        message: '활성화 확인 중 오류가 발생했습니다.'
      };
    }
  }

  /**
   * Activate a license on a device
   */
  async activateLicense(
    licenseKey: string, 
    deviceInfo: DeviceInfo
  ): Promise<LicenseActivationResult> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/license/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          licenseKey,
          deviceId: deviceInfo.deviceId,
          deviceInfo: {
            deviceName: deviceInfo.deviceName,
            platform: deviceInfo.platform,
            activatedAt: deviceInfo.activatedAt,
            lastActiveAt: deviceInfo.lastActiveAt,
            isCurrentDevice: deviceInfo.isCurrentDevice
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        // Get updated license info
        const validationResult = await this.verifyLicense(licenseKey);
        
        return {
          success: true,
          license: validationResult.license,
          deviceInfo
        };
      } else {
        return {
          success: false,
          error: {
            type: data.reason === 'device_limit_exceeded' ? 'device_limit_exceeded' : 'validation_failed',
            code: data.error || data.reason || 'activation_failed',
            message: data.message || '라이선스 활성화에 실패했습니다.',
            retryable: false,
            details: data.details
          },
          requiresDeviceDeactivation: data.reason === 'device_limit_exceeded',
          deviceInfo
        };
      }
    } catch (error) {
      return {
        success: false,
        error: {
          type: 'network_error',
          code: 'network_error',
          message: '라이선스 활성화 중 네트워크 오류가 발생했습니다.',
          retryable: true,
          details: error instanceof Error ? error.message : 'Unknown error'
        },
        deviceInfo
      };
    }
  }

  /**
   * Deactivate a license on a device
   */
  async deactivateLicense(licenseKey: string, deviceId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/license/deactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ licenseKey, deviceId })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message: '디바이스 비활성화 중 오류가 발생했습니다.'
      };
    }
  }

  /**
   * Get activated devices for a license
   */
  async getActivatedDevices(licenseKey: string): Promise<{
    devices: Array<{
      deviceId: string;
      activatedAt: string;
      [key: string]: any;
    }>;
    currentCount: number;
    maxDevices: number;
    plan: 'monthly' | 'yearly';
  }> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/license/devices/${encodeURIComponent(licenseKey)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to get activated devices:', error);
      return {
        devices: [],
        currentCount: 0,
        maxDevices: 3,
        plan: 'monthly'
      };
    }
  }

  /**
   * Get current device info
   */
  async getCurrentDeviceInfo(): Promise<DeviceInfo> {
    const platform = navigator.platform || 'Unknown';
    const userAgent = navigator.userAgent;
    
    // Detect OS
    let os = 'Unknown';
    let osVersion = '';
    
    if (userAgent.includes('Mac')) {
      os = 'macOS';
      const match = userAgent.match(/Mac OS X ([\d_]+)/);
      if (match) {
        osVersion = match[1].replace(/_/g, '.');
      }
    } else if (userAgent.includes('Win')) {
      os = 'Windows';
      const match = userAgent.match(/Windows NT ([\d.]+)/);
      if (match) {
        osVersion = match[1];
      }
    } else if (userAgent.includes('Linux')) {
      os = 'Linux';
    }

    // Generate device ID (stored in localStorage)
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = this.generateDeviceId();
      localStorage.setItem('device_id', deviceId);
    }

    return {
      deviceId,
      deviceName: `${os} Device`,
      platform: platform.toLowerCase().includes('mac') ? 'darwin' : 
               platform.toLowerCase().includes('win') ? 'win32' : 'linux',
      activatedAt: new Date(),
      lastActiveAt: new Date(),
      isCurrentDevice: true
    };
  }

  /**
   * Generate a unique device ID
   */
  private generateDeviceId(): string {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 15);
    const userAgent = navigator.userAgent;
    
    // Create a simple hash from user agent
    let hash = 0;
    for (let i = 0; i < userAgent.length; i++) {
      const char = userAgent.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    return `${timestamp}-${randomStr}-${Math.abs(hash).toString(36)}`;
  }
}